import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TenantAuthProvider, useTenantAuth } from './contexts/TenantAuthContext';
import { ReadOnlyProvider } from './contexts/ReadOnlyContext';
import { canAccess, isReadOnlyAccess } from './lib/permissions';
import { ToastContainer } from 'react-toastify';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CapLaunch from './pages/CapLaunch';
import Dashboard from './pages/Dashboard';
import DashboardHome from './pages/DashboardHome';
import AEODashboard from './pages/AEODashboard';
import PlatformDeepDive from './pages/PlatformDeepDive';
import Conversations from './pages/Conversations';
import Settings from './pages/Settings';
import AdvancedSettings from './pages/AdvancedSettings';
import ApiKeys from './pages/ApiKeys';
import CTAClicks from './pages/CTAClicks';
import MessageText from './pages/MessageText';
import Layout from './components/Layout';
import WidgetAutoLoader from './components/WidgetAutoLoader';
import { EventNotifierProvider } from './contexts/EventNotifierContext';
import { AeoReportProvider } from './contexts/AeoReportContext';
import Appointments from './pages/Appointments';
import KnowledgeBaseManagement from './pages/KnowledgeBaseManagement';
import ProductsManagement from './pages/ProductsManagement';
import ServicesManagement from './pages/ServicesManagement';
import EmbeddingsManagement from './pages/EmbeddingsManagement';
import AssetsManagement from './pages/AssetsManagement';
import PrimerQuestionsManagement from './pages/PrimerQuestionsManagement';
import CTAManagement from './pages/CTAManagement';
import ReviewManagement from './pages/ReviewManagement';
import HomePageConfigManagement from './pages/HomePageConfigManagement';
import Forms from './pages/Forms';
import Leads from './pages/Leads';
import ConversionEvents from './pages/ConversionEvents';
import PageLoader from './components/common/PageLoader';
import { NewFeaturesObserver } from './components/common';
import DashboardTabNav from './components/DashboardTabNav';
import ExpertsPage from './pages/expert/ExpertsPage';
import FlaggedAnswersPage from './pages/expert/FlaggedAnswersPage';
import TrainingPage from './pages/expert/TrainingPage';
import ExpertSettingsPage from './pages/expert/SettingsPage';
import { useWorkspace } from './hooks/useWorkspace';
import {
  EXPERT_HOME,
  EXPERT_PREFIX,
  EXPERT_ROUTES,
  getAvailableWorkspaces,
  getLandingPath,
  getWorkspaceFromPath,
  getWorkspaceHome,
} from './lib/workspace';
// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Permission-aware route guard.
 * - Checks authentication
 * - Uses the central permissions config (src/lib/permissions.ts) to determine access
 * - If 'hidden' → redirects to fallbackPath
 * - If 'read-only' → wraps children in ReadOnlyProvider
 * - If 'full' → renders children normally
 *
 * @param routePath - The route path to check permissions for (must match a key in permissions.ts)
 */
const PermissionRoute: React.FC<{
  children: React.ReactNode;
  routePath: string;
  fallbackPath?: string;
  hideBanner?: boolean;
}> = ({ children, routePath, fallbackPath = '/', hideBanner = false }) => {
  const { isAuthenticated, isLoading, admin, tenant } = useTenantAuth();

  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const role = admin?.role;

  const available = getAvailableWorkspaces(tenant, role);
  const routeWorkspace = getWorkspaceFromPath(routePath);
  if (available.length === 0) return <NoWorkspaceScreen />;
  if (!available.includes(routeWorkspace)) {
    return <Navigate to={getWorkspaceHome(available[0], role)} replace />;
  }

  // Hidden → redirect
  if (!canAccess(role, routePath)) {
    return <Navigate to={routeWorkspace === 'expert' ? getWorkspaceHome('expert', role) : fallbackPath} replace />;
  }

  // Read-only → wrap in ReadOnlyProvider
  const readOnly = isReadOnlyAccess(role, routePath);

  return (
    <Layout>
      <ReadOnlyProvider isReadOnly={readOnly} hideBanner={hideBanner}>
        {children}
      </ReadOnlyProvider>
    </Layout>
  );
};

const NoWorkspaceScreen: React.FC = () => {
  const { logout } = useTenantAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No access</h2>
        <p className="text-gray-500 mb-8">You don't have access to any section yet. Contact your administrator.</p>
        <button
          onClick={logout}
          className="inline-flex items-center px-6 py-2.5 border border-gray-300 text-sm font-medium shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

const AuthenticatedLanding: React.FC = () => {
  const { isLoading, tenant, admin } = useTenantAuth();
  if (isLoading) return <PageLoader />;
  return <Navigate to={getLandingPath(tenant, admin?.role)} replace />;
};

// Widget loader wrapper that knows about current route
const WidgetLoaderWrapper: React.FC = () => {
  const { isAuthenticated, tenant, admin } = useTenantAuth();
  const { workspace, hasClient } = useWorkspace();
  const [widgetApiKey, setWidgetApiKey] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const previewKey = JSON.parse(localStorage.getItem('widget_preview_api_key') ?? '{}');
        if (tenant?.id === previewKey?.tenant_id) {
          setWidgetApiKey(previewKey?.api_key);
        }
      } catch (error) {
        setWidgetApiKey(null);
      }
    };

    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widget-api-key-changed', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widget-api-key-changed', handleStorageChange);
    };
  }, [tenant]);

  const showErrorNotification = location.pathname === '/content-management';

  return (
    <WidgetAutoLoader
      key={admin?.id ?? 'unauthenticated'}
      enabled={isAuthenticated && hasClient && workspace === 'client'}
      apiKey={widgetApiKey}
      showErrorNotification={showErrorNotification}
    />
  );
};

// Error screen shown when permissions fail to load for non-super_admin roles
const PermissionsErrorScreen: React.FC = () => {
  const { retryPermissions, logout } = useTenantAuth();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    await retryPermissions();
    setRetrying(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Permissions failed to load</h2>
        <p className="text-gray-500 mb-8">
          We couldn't load your access permissions. Please try again or contact your administrator.
        </p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed w-28 justify-center"
          >
            {retrying ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Retrying…
              </>
            ) : (
              'Retry'
            )}
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center px-6 py-2.5 border border-gray-300 text-sm font-medium shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-28 justify-center"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

// Shell shared by all three dashboard views — renders DashboardTabNav once.
const DashboardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative">
    <DashboardTabNav />
    {children}
  </div>
);

// Main App component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, permissionsError, admin } = useTenantAuth();

  // Show error screen if permissions failed to load for non-super_admin roles
  // Only after loading is complete to avoid flashing before the API has responded
  const isSuperAdmin = admin?.role?.toLowerCase() === 'super_admin';
  if (!isLoading && isAuthenticated && permissionsError && !isSuperAdmin) {
    return <PermissionsErrorScreen />;
  }

  return (
    <>
      <Router>
        <WidgetLoaderWrapper />

        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <AuthenticatedLanding /> : <Login />}
          />
          <Route
            path="/forgot-password"
            element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />}
          />

          {/* /cap — headless launcher: mints the AEO PLATFORM handoff and redirects
              straight into CAP (admin-only; gating handled inside the page). */}
          <Route path="/cap" element={<CapLaunch />} />

          {/* Dashboard group — DashboardTabNav rendered once via DashboardShell */}
          <Route path="/" element={<PermissionRoute routePath="/"><DashboardShell><DashboardHome /></DashboardShell></PermissionRoute>} />
          <Route path="/dashboard/engagement" element={<PermissionRoute routePath="/dashboard/engagement"><DashboardShell><Dashboard /></DashboardShell></PermissionRoute>} />
          <Route path="/dashboard/aeo" element={<PermissionRoute routePath="/dashboard/aeo"><DashboardShell><AEODashboard /></DashboardShell></PermissionRoute>} />
          <Route path="/dashboard/aeo/:reportId/:slug" element={<PermissionRoute routePath="/dashboard/aeo"><PlatformDeepDive /></PermissionRoute>} />

          {/* Conversations */}
          <Route path="/conversations" element={<PermissionRoute routePath="/conversations"><Conversations /></PermissionRoute>} />

          {/* Content Management */}
          <Route path="/content-management/assets" element={<PermissionRoute routePath="/content-management/assets"><AssetsManagement /></PermissionRoute>} />
          <Route path="/content-management/questions" element={<PermissionRoute routePath="/content-management/questions"><PrimerQuestionsManagement /></PermissionRoute>} />
          <Route path="/content-management/ctas" element={<PermissionRoute routePath="/content-management/ctas"><CTAManagement /></PermissionRoute>} />
          <Route path="/content-management/reviews" element={<PermissionRoute routePath="/content-management/reviews"><ReviewManagement /></PermissionRoute>} />
          <Route path="/content-management/homepage" element={<PermissionRoute routePath="/content-management/homepage"><HomePageConfigManagement /></PermissionRoute>} />

          {/* Content Management redirects */}
          <Route path="/content-management" element={<Navigate to="/content-management/assets" replace />} />
          <Route path="/assets" element={<Navigate to="/content-management/assets" replace />} />
          <Route path="/data-management" element={<Navigate to="/content-management/assets" replace />} />

          {/* Settings */}
          <Route path="/settings" element={<PermissionRoute routePath="/settings" hideBanner><Settings /></PermissionRoute>} />
          <Route path="/settings/advanced" element={<PermissionRoute routePath="/settings/advanced"><AdvancedSettings /></PermissionRoute>} />

          {/* API Keys */}
          <Route path="/keys" element={<PermissionRoute routePath="/keys"><ApiKeys /></PermissionRoute>} />

          {/* Engagement Activity */}
          <Route path="/cta-clicks" element={<PermissionRoute routePath="/cta-clicks"><CTAClicks /></PermissionRoute>} />
          <Route path="/engagement-activity/forms" element={<PermissionRoute routePath="/engagement-activity/forms"><Forms /></PermissionRoute>} />
          <Route path="/engagement-activity/leads" element={<PermissionRoute routePath="/engagement-activity/leads"><Leads /></PermissionRoute>} />
          <Route path="/engagement-activity/events" element={<PermissionRoute routePath="/engagement-activity/events"><ConversionEvents /></PermissionRoute>} />

          {/* Appointments */}
          <Route path="/appointments" element={<PermissionRoute routePath="/appointments"><Appointments /></PermissionRoute>} />

          {/* Message Text */}
          <Route path="/message-text" element={<PermissionRoute routePath="/message-text"><MessageText /></PermissionRoute>} />

          {/* Knowledge Base */}
          <Route path="/knowledge-base/documents" element={<PermissionRoute routePath="/knowledge-base/documents"><KnowledgeBaseManagement /></PermissionRoute>} />
          <Route path="/knowledge-base/products" element={<PermissionRoute routePath="/knowledge-base/products"><ProductsManagement /></PermissionRoute>} />
          <Route path="/knowledge-base/services" element={<PermissionRoute routePath="/knowledge-base/services"><ServicesManagement /></PermissionRoute>} />
          <Route path="/knowledge-base/embeddings" element={<PermissionRoute routePath="/knowledge-base/embeddings"><EmbeddingsManagement /></PermissionRoute>} />

          {/* Domain Expert Agents */}
          <Route path={EXPERT_PREFIX} element={<Navigate to={EXPERT_HOME} replace />} />
          <Route path={EXPERT_ROUTES.experts} element={<PermissionRoute routePath={EXPERT_ROUTES.experts}><ExpertsPage /></PermissionRoute>} />
          <Route path={EXPERT_ROUTES.flaggedAnswers} element={<PermissionRoute routePath={EXPERT_ROUTES.flaggedAnswers}><FlaggedAnswersPage /></PermissionRoute>} />
          <Route path={EXPERT_ROUTES.training} element={<PermissionRoute routePath={EXPERT_ROUTES.training}><TrainingPage /></PermissionRoute>} />
          <Route path={EXPERT_ROUTES.homeScreen} element={<PermissionRoute routePath={EXPERT_ROUTES.homeScreen}><HomePageConfigManagement key="expert" mode="expert" /></PermissionRoute>} />
          <Route path={EXPERT_ROUTES.settings} element={<PermissionRoute routePath={EXPERT_ROUTES.settings}><ExpertSettingsPage /></PermissionRoute>} />

          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </>
  );
};

// Root App component with providers
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantAuthProvider>
        <EventNotifierProvider>
          <AeoReportProvider>
            <NewFeaturesObserver />
            <AppContent />
            <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
          </AeoReportProvider>
        </EventNotifierProvider>
      </TenantAuthProvider>
    </QueryClientProvider>
  );
}
